import React, { useMemo } from 'react';
import moment from 'moment';
import { SingleDatePicker } from '../../../CommonComponents/OPL/dates/SingleDatePicker/SingleDatePicker';
import { DATE_FORMAT } from '../../../CommonComponents/OPL/dates/utils';
import { Box } from '../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../CommonComponents/OPL/Button/Button';
import { ButtonGroup } from '../../../CommonComponents/OPL/ButtonGroup/ButtonGroup';
import { Select } from '../../../CommonComponents/OPL/Select/Select/Select';
import { Text } from '../../../CommonComponents/OPL/Text/Text';
import {
  ActivityDateTime,
  getNowAmPm,
  getNowHours,
  getNowMinutes,
  hoursOptions,
  minutesOptions,
} from './ActivityDateTimePicker.meta';

export interface ActivityDateTimePickerProps {
  value: ActivityDateTime;
  setActivityDateTime(data: ActivityDateTime): void;
}

export function ActivityDateTimePicker({
  value,
  setActivityDateTime,
}: ActivityDateTimePickerProps) {
  const pickerDate = useMemo(() => {
    const m = moment(value.date, DATE_FORMAT, true);
    return m.isValid() ? m.toDate() : undefined;
  }, [value.date]);

  return (
    <Box
      display="flex"
      flexWrap="wrap"
      justifyContent="space-between"
      css={({ theme }) => ({ gap: theme.space['16'] })}
    >
      <Box>
        <Text as="p" variant="formLabel" css="display: block;" mb="4">
          Date
        </Text>
        <SingleDatePicker
          date={pickerDate}
          maxDate={new Date()}
          onChange={(d: Date) => {
            const dateStr = moment(d).format(DATE_FORMAT);
            setActivityDateTime({ ...value, date: dateStr });
          }}
          isWrapperWidth
        />
        <Button
          type="button"
          variant="link"
          variantColor="primary"
          onClick={() => setActivityDateTime({ ...value, date: moment().format(DATE_FORMAT) })}
          mt="8"
        >
          Current Date
        </Button>
      </Box>

      <Box>
        <Text as="p" variant="formLabel" css="display: block;" mb="4">
          Time
        </Text>
        <Box
          display="flex"
          flexDirection="row"
          alignItems="flex-end"
          flexWrap="wrap"
          css={({ theme }) => ({ gap: theme.space['8'] })}
        >
          <Box flex="1" maxWidth="72px">
            <Select
              aria-label="Hour"
              placeholder="00"
              value={hoursOptions.find(option => option.value === value.hours) ?? null}
              options={hoursOptions}
              onChange={option => {
                if (option == null) {
                  return;
                }
                setActivityDateTime({ ...value, hours: option.value });
              }}
              isPortal
            />
          </Box>
          <Box flex="1" maxWidth="72px">
            <Select
              aria-label="Minute"
              placeholder="00"
              value={minutesOptions.find(option => option.value === value.minutes) ?? null}
              options={minutesOptions}
              onChange={option => {
                if (option == null) {
                  return;
                }
                setActivityDateTime({ ...value, minutes: option.value });
              }}
              isPortal
            />
          </Box>
          <ButtonGroup isAttached role="group" aria-label="AM or PM">
            <Button
              type="button"
              variant={value.amPm === 'AM' ? 'primary' : 'secondary'}
              onClick={() => setActivityDateTime({ ...value, amPm: 'AM' })}
              aria-pressed={value.amPm === 'AM'}
            >
              AM
            </Button>
            <Button
              type="button"
              variant={value.amPm === 'PM' ? 'primary' : 'secondary'}
              onClick={() => setActivityDateTime({ ...value, amPm: 'PM' })}
              aria-pressed={value.amPm === 'PM'}
            >
              PM
            </Button>
          </ButtonGroup>
        </Box>
        <Button
          type="button"
          variant="link"
          variantColor="primary"
          onClick={() =>
            setActivityDateTime({
              ...value,
              hours: getNowHours(),
              minutes: getNowMinutes(),
              amPm: getNowAmPm(),
            })
          }
          mt="8"
        >
          Current Time
        </Button>
      </Box>
    </Box>
  );
}
